import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from, BehaviorSubject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { DataService } from '../data.service';
import { ProfileService } from '../profile/profile.service';
import Talk from 'talkjs';

declare var AppStorageUtil: any;

export interface TalkJSConfig {
  appId: string;
  enabled: boolean;
  secretKey?: string;
}

export interface TalkJSTokenResponse {
  token: string;
}

/** Show the composer unless TalkJS custom.mode is "read" (including when mode is unset). */
export const TALKJS_WRITABLE_MESSAGE_FIELD_VISIBLE = [
  "any",
  [
    { custom: { mode: "!exists" } },
    { custom: { mode: ["!=", "read"] } },
  ],
] as Talk.ConversationPredicate;

export function isTalkJsReadOnlyConversation(
  conversation: {
    custom?: Record<string, unknown> | null;
    subject?: string | null;
  } | null | undefined
): boolean {
  if (!conversation) {
    return false;
  }

  const mode = String(
    conversation.custom?.["mode"] ?? conversation.custom?.["Mode"] ?? ""
  )
    .trim()
    .toLowerCase();
  if (mode === "read") {
    return true;
  }

  return String(conversation.subject || "")
    .toLowerCase()
    .includes("counter offer");
}

@Injectable({
  providedIn: 'root'
})
export class TalkJSService {
  private moduleUrl = '';
  private appId: string | null = null;
  private enabled: boolean = false;
  private session: Talk.Session | null = null;

  // Observable for unread message count
  private unreadCountSubject = new BehaviorSubject<number>(0);
  public unreadCount$ = this.unreadCountSubject.asObservable();

  constructor(
    private http: HttpClient,
    private _dataService: DataService,
    private _profileService: ProfileService
  ) { }

  /**
   * Get TalkJS configuration from backend
   */
  getConfig(): Observable<TalkJSConfig> {
    this.moduleUrl = 'TalkJS/config';
    return this._dataService.getAll<TalkJSConfig>(this.moduleUrl).pipe(
      map(config => {
        this.appId = config.appId;
        this.enabled = config.enabled;
        return config;
      })
    );
  }

  /**
   * Generate user token from backend
   */
  generateUserToken(userId: string): Observable<string> {
    this.moduleUrl = 'TalkJS/generate-token';
    return this._dataService.add<TalkJSTokenResponse>(this.moduleUrl, { userId }).pipe(
      map(response => response.token)
    );
  }

  /**
   * Create or update a TalkJS user via backend
   */
  createOrUpdateUser(userId: string, userData: any): Observable<any> {
    this.moduleUrl = `TalkJS/users/${userId}`;
    return this._dataService.updatePass(this.moduleUrl, userData);
  }

  /**
   * Create or update a conversation via backend
   */
  createOrUpdateConversation(conversationId: string, conversationData: any): Observable<any> {
    this.moduleUrl = `TalkJS/conversations/${conversationId}`;
    return this._dataService.updatePass(this.moduleUrl, conversationData);
  }

  /**
   * Initialize TalkJS session for current user
   */
  async initializeChat(
    containerId: string,
    conversationId: string,
    otherUserId: string,
    otherUserName: string,
    otherUserEmail?: string,
    rfqGuid?: string,
    userRole?: string,
    otherUserCompanyName?: string,
    composerMessageSuggestion?: string,
    forceReadWrite = false,
    /** When rfqId is 0 (supplier network), used as TalkJS subject instead of "RFQ No. 0 - …". */
    networkChatSubject?: string
  ): Promise<void> {
    try {
      // Wait for TalkJS to be ready
      await Talk.ready;

      let hasDraftMessage = composerMessageSuggestion && composerMessageSuggestion.trim() !== '';

      // Use shared session if available, otherwise initialize it
      let session = this.session;
      if (!session) {
        await this.initializeSessionForUnreadTracking();
        session = this.session;
      }

      if (!session) {
        console.error('Failed to get or create TalkJS session for chat');
        return;
      }

      // Get current user ID and role from storage
      const currentUserId = AppStorageUtil.getItem('LoggedId');
      const currentUserRole = AppStorageUtil.getItem('Usertype');
      if (!currentUserId) {
        console.error('No logged in user');
        return;
      }

      // Debug: Log the user role to help diagnose issues
      console.log('TalkJS: Current user role from storage:', currentUserRole);
      console.log('TalkJS: User ID from storage:', currentUserId);

      // Validate otherUserId - TalkJS requires a non-empty string or number
      if (!otherUserId || (typeof otherUserId === 'string' && otherUserId.trim() === '')) {
        console.error('TalkJS: otherUserId is required and must be a non-empty string or number. Received:', otherUserId);
        throw new Error('Other user ID is required for chat initialization');
      }

      // Ensure otherUserId is a string (TalkJS accepts both string and number, but we'll use string for consistency)
      const otherUserIdStr = String(otherUserId).trim();
      if (!otherUserIdStr) {
        console.error('TalkJS: otherUserId is empty after conversion to string');
        throw new Error('Other user ID cannot be empty');
      }

      // Format name with company name if available.
      // This keeps buyer/supplier conversation previews consistent in TalkJS inbox.
      const formattedName = this.formatNameWithCompany(otherUserName || 'Contact', otherUserCompanyName);

      // Create Talk.User for other user (use 'default' role to ensure all permissions are enabled)
      const other = new Talk.User({
        id: otherUserIdStr, // Use validated string version
        name: formattedName,
        email: otherUserEmail || `${otherUserIdStr}@example.com`,
        role: 'default',  // Use default role to ensure file uploads and all features work
        photoUrl: 'https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y'
      });

      // Ensure other user exists via backend
      await this.createOrUpdateUser(other.id, {
        id: other.id,
        name: other.name,
        email: other.email,
        role: 'default',
        photoUrl: other.photoUrl
      }).toPromise();

      // Extract plain RFQ ID from conversation ID when available.
      // RFQ format:  rfq_<rfqId>_<userId1>_<userId2> (bidirectional, sorted user IDs)
      // Generic chat: chat_<userId1>_<userId2>
      let plainRfqId: string | null = null;
      if (conversationId.startsWith('rfq_')) {
        const conversationIdParts = conversationId.split('_');
        const extractedRfqId = conversationIdParts.length >= 2 ? conversationIdParts[1] : null;
        // Only use RFQ ID if it's valid (not null, not empty, and not "0")
        if (extractedRfqId && extractedRfqId !== '0' && extractedRfqId.trim() !== '') {
          plainRfqId = extractedRfqId;
        }
      }

      // Ensure all custom data fields are strings (TalkJS requirement)
      const safeRfqGuid = rfqGuid ? String(rfqGuid) : '';

      // Get and normalize user role from storage - explicitly check for buyer/supplier
      // This ensures user_role is correctly set based on the logged-in user
      let safeUserRole = 'unknown';
      if (currentUserRole) {
        const normalizedRole = String(currentUserRole).trim().toLowerCase();
        // Explicitly check for buyer role (case-insensitive)
        if (normalizedRole === 'buyer') {
          safeUserRole = 'buyer';
          console.log('TalkJS: User role detected as BUYER from storage');
        } else if (normalizedRole === 'supplier') {
          safeUserRole = 'supplier';
          console.log('TalkJS: User role detected as SUPPLIER from storage');
        } else {
          // Try to get from applicableRoles if Usertype is not clear
          const applicableRoles = AppStorageUtil.getItem('applicableRoles');
          if (applicableRoles) {
            try {
              const rolesArray = JSON.parse(applicableRoles);
              if (Array.isArray(rolesArray)) {
                // Check for Buyer (capitalized) in applicableRoles
                if (rolesArray.includes('Buyer')) {
                  safeUserRole = 'buyer';
                  console.log('TalkJS: User role detected as BUYER from applicableRoles');
                } else if (rolesArray.includes('Supplier')) {
                  safeUserRole = 'supplier';
                  console.log('TalkJS: User role detected as SUPPLIER from applicableRoles');
                }
              }
            } catch (e) {
              console.warn('TalkJS: Could not parse applicableRoles:', e);
            }
          }
          // If still unknown, use the normalized role
          if (safeUserRole === 'unknown') {
            safeUserRole = normalizedRole;
            console.warn('TalkJS: Using normalized role as fallback:', safeUserRole);
          }
        }
      } else {
        // If Usertype is not set, try to get from applicableRoles
        const applicableRoles = AppStorageUtil.getItem('applicableRoles');
        if (applicableRoles) {
          try {
            const rolesArray = JSON.parse(applicableRoles);
            if (Array.isArray(rolesArray)) {
              if (rolesArray.includes('Buyer')) {
                safeUserRole = 'buyer';
                console.log('TalkJS: User role detected as BUYER from applicableRoles (Usertype was empty)');
              } else if ( rolesArray.includes('Supplier')) {
                safeUserRole = 'supplier';
                console.log('TalkJS: User role detected as SUPPLIER from applicableRoles (Usertype was empty)');
              }
            }
          } catch (e) {
            console.warn('TalkJS: Could not parse applicableRoles:', e);
          }
        }
      }

      // Debug: Log the final normalized role
      console.log('TalkJS: Final normalized user role:', safeUserRole, '(from storage Usertype:', currentUserRole, ')');

      // Create or get conversation
      const conversation = session.getOrCreateConversation(conversationId);

      // Determine account type to optionally make chat read-only
      const accountType = AppStorageUtil.getItem('AccountType');
      const isBasicAccount = accountType === 'Basic';

      // Set only the two intended participants for one-on-one chat
      // IMPORTANT: Only set exactly 2 participants to prevent TalkJS from auto-converting to group chat
      // TalkJS automatically converts conversations to group chat when there are more than 2 participants
      // By ensuring we only add these 2 participants, we maintain a one-on-one conversation
      //
      // For Basic accounts, make the current user read-only in this conversation.
      // TalkJS will automatically disable the composer / sending messages in the UI.
      // Buyer network and other explicit flows can opt out via forceReadWrite.
      if (isBasicAccount && !forceReadWrite) {
        conversation.setParticipant(session.me, { access: 'Read' });
      } else {
        conversation.setParticipant(session.me, { access: 'ReadWrite' });
      }

      // The "other" participant should always be able to reply.
      conversation.setParticipant(other, { access: 'ReadWrite' });

      // Note: If group chat is still being triggered, it may be because:
      // 1. The backend is adding additional participants via createOrUpdateConversation
      // 2. The conversation already exists with more than 2 participants from previous interactions
      // 3. Multiple users are somehow sharing the same conversationId

      // Set conversation attributes
      // Set conversation attributes. If we have an RFQ, keep existing RFQ-based subject and links.
      // Otherwise, use a generic subject and leave RFQ fields empty.
      // Only include RFQ-related fields when we have a valid RFQ ID (not network chat rfq_0_*)
      const hasValidRfq = plainRfqId && plainRfqId !== '0' && plainRfqId.trim() !== '';
      const isSupplierNetworkChat =
        conversationId.startsWith('rfq_0_') ||
        (conversationId.startsWith('rfq_') &&
          conversationId.split('_')[1] === '0');

      // For non-RFQ conversations, don't set subject (let TalkJS auto-generate from participant names)
      // This prevents duplication issues with formattedSubject showing "Direct Message Direct Message"
      // IMPORTANT: Always set user_role to the CURRENT user's role, not the role of who created the conversation
      // This ensures that when a buyer opens a conversation, it shows 'buyer', and when a supplier opens it, it shows 'supplier'
      const conversationAttributes: any = {
        custom: {
          user_role: safeUserRole,  // Current user's role - always update to reflect who is viewing
          has_draft_message: hasDraftMessage ? 'true' : 'false'
        }
      };

      // Debug: Log what we're setting
      console.log('TalkJS: Setting conversation custom.user_role to:', safeUserRole);

      // Only set subject and RFQ-related custom fields if we have a valid RFQ
      if (hasValidRfq) {
        conversationAttributes.subject = `RFQ No. ${plainRfqId} - Message`;
        conversationAttributes.custom.rfqId = String(plainRfqId);
        conversationAttributes.custom.rfq_guid = safeRfqGuid || '';

        // IMPORTANT: Always set rfq_url to prevent TalkJS from generating its own internal URL
        // Even if rfqGuid is empty, we can still construct URLs using rfqId to avoid TalkJS default URLs
        if (safeRfqGuid) {
          // We have rfqGuid - use it to construct proper URLs
          const buyerUrl = `${window.location.origin}/#/rfq/rfqdetail?rfqId=${encodeURIComponent(safeRfqGuid)}&quotes=Quotes`;
          const supplierUrl = `${window.location.origin}/#/supplier/supplierRfqDetails?rfqId=${encodeURIComponent(safeRfqGuid)}&rfq=RFQ`;

          conversationAttributes.custom.rfq_url_buyer = buyerUrl;
          conversationAttributes.custom.rfq_url_supplier = supplierUrl;

          // Set the single rfq_url based on current user's role from storage
          // IMPORTANT: If logged in as buyer → rfq_url = rfq_url_buyer
          //            If logged in as supplier → rfq_url = rfq_url_supplier
          // This prevents TalkJS from generating its own internal URL (https://app.talkjs.com/...)
          if (safeUserRole === 'buyer') {
            // User is logged in as buyer - assign rfq_url_buyer to rfq_url
            conversationAttributes.custom.rfq_url = buyerUrl;
            console.log('TalkJS: User is BUYER - Setting rfq_url to rfq_url_buyer:', buyerUrl);
            console.log('TalkJS: rfq_url_buyer value:', conversationAttributes.custom.rfq_url_buyer);
            console.log('TalkJS: rfq_url assigned value:', conversationAttributes.custom.rfq_url);
          } else if (safeUserRole === 'supplier') {
            // User is logged in as supplier - assign rfq_url_supplier to rfq_url
            conversationAttributes.custom.rfq_url = supplierUrl;
            console.log('TalkJS: User is SUPPLIER - Setting rfq_url to rfq_url_supplier:', supplierUrl);
            console.log('TalkJS: rfq_url_supplier value:', conversationAttributes.custom.rfq_url_supplier);
            console.log('TalkJS: rfq_url assigned value:', conversationAttributes.custom.rfq_url);
          } else {
            // Fallback: if role is unknown, default to buyer URL (most common case)
            // This ensures rfq_url is always set and prevents TalkJS URL from being used
            conversationAttributes.custom.rfq_url = buyerUrl;
            console.warn('TalkJS: Unknown user role (' + safeUserRole + '), defaulting to buyer URL:', buyerUrl);
            console.warn('TalkJS: Original role from storage was:', currentUserRole);
          }

          // Debug: Verify rfq_url is set
          console.log('TalkJS: Final rfq_url value:', conversationAttributes.custom.rfq_url);
          console.log('TalkJS: User role used for rfq_url:', safeUserRole, '(original:', currentUserRole, ')');
        } else if (plainRfqId) {
          // rfqGuid is empty but we have rfqId - construct URL using rfqId to prevent TalkJS default URL
          // This prevents TalkJS from generating: https://app.talkjs.com/app/tjDnh41h/user/.../chatbox/...
          console.warn('TalkJS: No rfqGuid provided, but rfqId exists. Constructing URL with rfqId to prevent TalkJS default URL.');

          // Try to get rfqGuid from storage as fallback
          const rfqGuidFromStorage = AppStorageUtil.getItem('rfqGuid') || '';
          if (rfqGuidFromStorage) {
            const buyerUrl = `${window.location.origin}/#/rfq/rfqdetail?rfqId=${encodeURIComponent(rfqGuidFromStorage)}&quotes=Quotes`;
            const supplierUrl = `${window.location.origin}/#/supplier/supplierRfqDetails?rfqId=${encodeURIComponent(rfqGuidFromStorage)}&rfq=RFQ`;

            conversationAttributes.custom.rfq_url_buyer = buyerUrl;
            conversationAttributes.custom.rfq_url_supplier = supplierUrl;

            // Set rfq_url based on logged-in user's role
            // If buyer → rfq_url = rfq_url_buyer
            // If supplier → rfq_url = rfq_url_supplier
            if (safeUserRole === 'buyer') {
              conversationAttributes.custom.rfq_url = buyerUrl;
              console.log('TalkJS: User is BUYER - Setting rfq_url to rfq_url_buyer (from storage):', buyerUrl);
            } else if (safeUserRole === 'supplier') {
              conversationAttributes.custom.rfq_url = supplierUrl;
              console.log('TalkJS: User is SUPPLIER - Setting rfq_url to rfq_url_supplier (from storage):', supplierUrl);
            } else {
              conversationAttributes.custom.rfq_url = buyerUrl;
              console.warn('TalkJS: Unknown role, defaulting to buyer URL');
            }
          } else {
            // Last resort: encrypt plain rfqId to get proper encrypted GUID format
            // Supplier URLs MUST use encrypted format (e.g., YVwMDIL%2FMMwOlxfIUWn7qQ%3D%3D)
            // NOT plain numeric ID (e.g., 1213180)
            try {
              const encryptedRfqId = this._profileService.encrypt(plainRfqId).toString();
              const buyerUrl = `${window.location.origin}/#/rfq/rfqdetail?rfqId=${encodeURIComponent(encryptedRfqId)}&quotes=Quotes`;
              const supplierUrl = `${window.location.origin}/#/supplier/supplierRfqDetails?rfqId=${encodeURIComponent(encryptedRfqId)}&rfq=RFQ`;

              conversationAttributes.custom.rfq_url_buyer = buyerUrl;
              conversationAttributes.custom.rfq_url_supplier = supplierUrl;

              // Set rfq_url based on logged-in user's role
              // If buyer → rfq_url = rfq_url_buyer
              // If supplier → rfq_url = rfq_url_supplier
              if (safeUserRole === 'buyer') {
                conversationAttributes.custom.rfq_url = buyerUrl;
                console.log('TalkJS: User is BUYER - Setting rfq_url to rfq_url_buyer (using encrypted rfqId):', buyerUrl);
              } else if (safeUserRole === 'supplier') {
                conversationAttributes.custom.rfq_url = supplierUrl;
                console.log('TalkJS: User is SUPPLIER - Setting rfq_url to rfq_url_supplier (using encrypted rfqId):', supplierUrl);
                console.log('TalkJS: Encrypted rfqId:', encryptedRfqId, '(from plain rfqId:', plainRfqId + ')');
              } else {
                conversationAttributes.custom.rfq_url = buyerUrl;
                console.warn('TalkJS: Unknown role, defaulting to buyer URL');
              }
              console.log('TalkJS: Successfully encrypted plain rfqId:', plainRfqId, '→', encryptedRfqId);
            } catch (error) {
              console.error('TalkJS: ERROR - Failed to encrypt rfqId:', error);
              console.error('TalkJS: Cannot construct proper URLs without encrypted GUID');
              // Set empty URLs to prevent incorrect URLs
              conversationAttributes.custom.rfq_url_buyer = '';
              conversationAttributes.custom.rfq_url_supplier = '';
              conversationAttributes.custom.rfq_url = '';
              console.warn('TalkJS: Leaving rfq_url empty - encryption failed');
            }
          }
        } else {
          // No RFQ data at all - set empty strings (this is OK for non-RFQ conversations)
          conversationAttributes.custom.rfq_url_buyer = '';
          conversationAttributes.custom.rfq_url_supplier = '';
          conversationAttributes.custom.rfq_url = '';
          console.warn('TalkJS: No RFQ data available, rfq_url will be empty (this is OK for non-RFQ conversations)');
        }
      } else if (isSupplierNetworkChat) {
        // Supplier/buyer network (rfqId 0): avoid "RFQ No. 0" in the TalkJS chat header
        conversationAttributes.subject =
          networkChatSubject?.trim() || 'Supplier network';
        conversationAttributes.custom.rfqId = '';
        conversationAttributes.custom.rfq_guid = '';
        conversationAttributes.custom.rfq_url_buyer = '';
        conversationAttributes.custom.rfq_url_supplier = '';
        conversationAttributes.custom.rfq_url = '';
      } else {
        // When there's no RFQ, set rfqId to empty string (required by TalkJS for string fields)
        conversationAttributes.custom.rfqId = '';
        conversationAttributes.custom.rfq_guid = '';
        conversationAttributes.custom.rfq_url_buyer = '';
        conversationAttributes.custom.rfq_url_supplier = '';
        conversationAttributes.custom.rfq_url = '';
        // Don't set subject - let TalkJS auto-generate from participant names
      }

      // Final validation: Ensure rfq_url is always set when we have a valid RFQ
      if (hasValidRfq && safeRfqGuid && !conversationAttributes.custom.rfq_url) {
        console.error('TalkJS: ERROR - rfq_url is empty but we have a valid RFQ!');
        console.error('TalkJS: safeUserRole:', safeUserRole);
        console.error('TalkJS: currentUserRole:', currentUserRole);
        // Fallback: set to buyer URL as default
        const fallbackUrl = `${window.location.origin}/#/rfq/rfqdetail?rfqId=${encodeURIComponent(safeRfqGuid)}&rfq=RFQ`;
        conversationAttributes.custom.rfq_url = fallbackUrl;
        console.error('TalkJS: Setting fallback rfq_url:', fallbackUrl);
      }

      // Always update conversation attributes to ensure user_role reflects the current user
      // This is important because conversations are shared, and we want the role to match who is viewing
      // IMPORTANT: We always set user_role to the CURRENT user's role, not the role of who created the conversation
      // This ensures that when a buyer opens a conversation, it shows 'buyer', and when a supplier opens it, it shows 'supplier'
      conversation.setAttributes(conversationAttributes);

      // Debug: Log what we're setting
      console.log('TalkJS: Conversation attributes being set:', {
        user_role: conversationAttributes.custom.user_role,
        rfqId: conversationAttributes.custom.rfqId,
        rfq_url: conversationAttributes.custom.rfq_url,
        rfq_url_buyer: conversationAttributes.custom.rfq_url_buyer,
        rfq_url_supplier: conversationAttributes.custom.rfq_url_supplier,
        hasValidRfq: hasValidRfq,
        safeUserRole: safeUserRole,
        currentUserRole: currentUserRole
      });

      // Sync conversation attributes with backend to ensure subject is properly saved
      // Note: The backend should preserve the user_role we send, but if it overrides it,
      // we'll need to check the backend API implementation
      // Only sync with backend if we have valid data (avoid sending empty rfqGuid when rfqId exists)
      // This prevents 500 errors from the backend when required fields are missing
      const shouldSyncWithBackend = !hasValidRfq || (hasValidRfq && safeRfqGuid && safeRfqGuid.trim() !== '');
      if (shouldSyncWithBackend) {
        try {
          await this.createOrUpdateConversation(conversationId, conversationAttributes).toPromise();
          console.log('TalkJS: Successfully synced conversation attributes with backend');
        } catch (error) {
          console.error('Failed to sync conversation with backend:', error);
          // Don't throw - allow chat to continue even if backend sync fails
          // The local TalkJS conversation will still work
        }
      } else {
        console.warn('TalkJS: Skipping backend sync - rfqId is present but rfqGuid is missing');
        console.warn('TalkJS: This may happen if RFQ details are still loading. Chat will work locally.');
      }

      // Show a welcome message when the conversation has no real messages yet.
      // TalkJS displays these only when the message list is empty; they disappear once a real message is sent.
      const welcomeMessage = hasDraftMessage
        ? 'No messages yet. Your prepared draft is ready below.'
        : 'No messages yet. Start the conversation below.';
      conversation.setAttributes({ welcomeMessages: [welcomeMessage] });

      const trimmedSuggestion = composerMessageSuggestion?.trim();
      const chatbox = session.createChatbox({
        showChatHeader: true,
        messageField: {
          visible: TALKJS_WRITABLE_MESSAGE_FIELD_VISIBLE,
        },
        ...(trimmedSuggestion ? { messageSuggestion: trimmedSuggestion } : {}),
      });
      chatbox.select(conversation);
      chatbox.mount(document.getElementById(containerId));

      console.log('TalkJS chatbox mounted successfully');
      console.log('App ID:', this.appId);
      console.log('Conversation ID:', conversationId);
      console.log('Session User:', { id: session.me.id, name: session.me.name, role: session.me.role });
      console.log('Other User:', { id: other.id, name: other.name, role: other.role });
      console.log('Custom Data:', {
        rfqId: plainRfqId,
        rfq_guid: safeRfqGuid,
        user_role: safeUserRole
      });

      // Wait a bit and then check the iframe
      setTimeout(() => {
        const container = document.getElementById(containerId);
        const iframe = container?.querySelector('iframe');
        if (iframe) {
          console.log('TalkJS iframe found:', {
            src: iframe.src,
            width: iframe.offsetWidth,
            height: iframe.offsetHeight
          });
        } else {
          console.error('TalkJS iframe NOT found in container:', containerId);
        }

        console.log('='.repeat(80));
        console.log('FILE UPLOAD TROUBLESHOOTING:');
        console.log('1. Check if you see these icons at the bottom: 📍 📎 😊 ➤');
        console.log('2. If 📎 is missing, check TalkJS Dashboard → Chat UI → Roles');
        console.log('3. All users are now using the "default" role');
        console.log('4. File uploads should be visible if enabled in your TalkJS dashboard');
        console.log('='.repeat(80));
      }, 2000);

    } catch (error) {
      console.error('Error initializing TalkJS chat:', error);
    }
  }

  /**
   * Check if TalkJS is enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Get App ID
   */
  getAppId(): string | null {
    return this.appId;
  }

  /**
   * Initialize session and start tracking unread messages
   */
  async initializeSessionForUnreadTracking(): Promise<void> {
    try {
      await Talk.ready;

      const config = await this.getConfig().toPromise();
      if (!config.enabled) {
        return;
      }

      const currentUserId = AppStorageUtil.getItem('LoggedId');
      const userType = AppStorageUtil.getItem('Usertype');
      const accountType = AppStorageUtil.getItem('AccountType') || '';
      const iContactViewModel = JSON.parse(AppStorageUtil.getItem('iContactViewModel') || '{}');
      const currentUserName = iContactViewModel.firstName && iContactViewModel.lastName
        ? `${iContactViewModel.firstName} ${iContactViewModel.lastName}`.trim()
        : AppStorageUtil.getItem('loggedContactName') || 'Current User';
      const currentUserCompanyName = (iContactViewModel.company && iContactViewModel.company.name)
        ? iContactViewModel.company.name
        : (AppStorageUtil.getItem('loggedCompanyName') || '');
      const currentUserEmail = iContactViewModel.email || AppStorageUtil.getItem('loggedContactEmail') || `${currentUserId}@example.com`;

      if (!currentUserId) {
        return;
      }

      const token = await this.generateUserToken(currentUserId).toPromise();
      if (!token) {
        return;
      }

      // Map MFG account type to TalkJS role.
      // IMPORTANT: Make sure a matching "basic" role exists in the TalkJS dashboard
      // and is configured there as read-only (no composer / no sending).
      const talkJsRole = accountType === 'Basic' ? 'basic' : 'default';

      const me = new Talk.User({
        id: currentUserId,
        name: this.formatNameWithCompany(currentUserName, currentUserCompanyName),
        email: currentUserEmail,
        role: talkJsRole,
        photoUrl: 'https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y'
      });

      const sessionOptions: any = {
        appId: config.appId,
        me: me
      };

      // Only add signature for production apps (App IDs that don't start with 'tj')
      if (!config.appId.startsWith('tj')) {
        sessionOptions.signature = token;
      }

      this.session = new Talk.Session(sessionOptions);

      // Subscribe to unread conversation changes
      this.session.unreads.onChange((unreadConversations) => {
        const totalUnread = unreadConversations.length;
        this.unreadCountSubject.next(totalUnread);
      });

      // Enable browser notifications
      await this.enableBrowserNotifications();

    } catch (error) {
      console.error('Error initializing TalkJS session for unread tracking:', error);
    }
  }

  /**
   * Request browser notification permission and enable TalkJS notifications
   */
  private async enableBrowserNotifications(): Promise<void> {
    try {
      // Check if the browser supports notifications
      if (!('Notification' in window)) {
        console.warn('This browser does not support desktop notifications');
        return;
      }

      // Check current permission status
      if (Notification.permission === 'granted') {
        console.log('Notification permission already granted');
        this.setupTalkJSNotifications();
        return;
      }

      // Request permission if not denied
      if (Notification.permission !== 'denied') {
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
          console.log('Notification permission granted');
          this.setupTalkJSNotifications();
        } else {
          console.log('Notification permission denied by user');
        }
      } else {
        console.log('Notification permission was previously denied');
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
    }
  }

  /**
   * Setup TalkJS notification handlers
   */
  private setupTalkJSNotifications(): void {
    if (!this.session) {
      return;
    }

    try {
      // Enable TalkJS built-in notifications
      this.session.setDesktopNotificationEnabled(true);

      // Optional: Listen for new messages and show custom notifications
      this.session.unreads.onChange((conversations) => {
        // TalkJS will automatically show notifications for unread messages
        // But we can add custom logic here if needed
        console.log(`You have ${conversations.length} unread conversation(s)`);
      });

      console.log('TalkJS desktop notifications enabled');
    } catch (error) {
      console.error('Error setting up TalkJS notifications:', error);
    }
  }

  /**
   * Get current unread count
   */
  getUnreadCount(): number {
    return this.unreadCountSubject.value;
  }

  /**
   * Get the current session (for use in inbox/chatbox)
   */
  getSession(): Talk.Session | null {
    return this.session;
  }

  private formatNameWithCompany(name: string, companyName?: string): string {
    const baseName = (name || 'Contact').trim();
    const trimmedCompany = (companyName || '').trim();
    if (!trimmedCompany) {
      return baseName;
    }

    const normalizedBaseName = baseName.toLowerCase();
    const normalizedCompany = trimmedCompany.toLowerCase();
    if (normalizedBaseName.includes(` from ${normalizedCompany}`) || normalizedBaseName.endsWith(` (${normalizedCompany})`)) {
      return baseName;
    }

    return `${baseName} from ${trimmedCompany}`;
  }

  /**
   * Request notification permission (can be called manually from UI)
   */
  async requestNotificationPermission(): Promise<boolean> {
    try {
      if (!('Notification' in window)) {
        console.warn('This browser does not support desktop notifications');
        return false;
      }

      if (Notification.permission === 'granted') {
        this.setupTalkJSNotifications();
        return true;
      }

      if (Notification.permission === 'denied') {
        console.log('Notification permission was denied. User must enable it in browser settings.');
        return false;
      }

      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        this.setupTalkJSNotifications();
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }

  /**
   * Check if browser notifications are enabled
   */
  areNotificationsEnabled(): boolean {
    if (!('Notification' in window)) {
      return false;
    }
    return Notification.permission === 'granted';
  }

  /**
   * Get notification permission status
   */
  getNotificationPermission(): NotificationPermission | 'unsupported' {
    if (!('Notification' in window)) {
      return 'unsupported';
    }
    return Notification.permission;
  }

  /**
   * Update conversation attributes based on current user's role
   * This ensures user_role and rfq_url are correct when viewing conversations in inbox
   */
  async updateConversationAttributesForCurrentUser(
    conversationIdOrConversation:
      | string
      | { id: string; custom?: Record<string, unknown> | null; subject?: string | null }
  ): Promise<void> {
    try {
      if (!conversationIdOrConversation) {
        return;
      }

      // Get session - required to access conversation
      const session = this.session;
      if (!session) {
        console.warn('TalkJS: No session available to update conversation attributes');
        return;
      }

      let conversationId: string;
      let selectedCustom: Record<string, unknown> = {};
      let selectedSubject = '';
      if (typeof conversationIdOrConversation === 'string') {
        conversationId = conversationIdOrConversation;
      } else {
        conversationId = conversationIdOrConversation.id;
        selectedCustom = { ...(conversationIdOrConversation.custom || {}) };
        selectedSubject = conversationIdOrConversation.subject || '';
      }

      if (
        isTalkJsReadOnlyConversation({
          custom: selectedCustom,
          subject: selectedSubject,
        })
      ) {
        return;
      }

      // Get the conversation object from session - this has setAttributes method
      const conversation = session.getOrCreateConversation(conversationId);

      // Get current user role (reuse the same logic from initializeChat)
      const currentUserRole = AppStorageUtil.getItem('Usertype');
      let safeUserRole = 'unknown';

      if (currentUserRole) {
        const normalizedRole = String(currentUserRole).trim().toLowerCase();
        if (normalizedRole === 'buyer') {
          safeUserRole = 'buyer';
        } else if (normalizedRole === 'supplier') {
          safeUserRole = 'supplier';
        } else {
          // Try to get from applicableRoles if Usertype is not clear
          const applicableRoles = AppStorageUtil.getItem('applicableRoles');
          if (applicableRoles) {
            try {
              const rolesArray = JSON.parse(applicableRoles);
              if (Array.isArray(rolesArray)) {
                if (rolesArray.includes('Buyer')) {
                  safeUserRole = 'buyer';
                } else if (rolesArray.includes('Supplier')) {
                  safeUserRole = 'supplier';
                }
              }
            } catch (e) {
              console.warn('TalkJS: Could not parse applicableRoles:', e);
            }
          }
          if (safeUserRole === 'unknown') {
            safeUserRole = normalizedRole;
          }
        }
      } else {
        // If Usertype is not set, try to get from applicableRoles
        const applicableRoles = AppStorageUtil.getItem('applicableRoles');
        if (applicableRoles) {
          try {
            const rolesArray = JSON.parse(applicableRoles);
            if (Array.isArray(rolesArray)) {
              if (rolesArray.includes('Buyer')) {
                safeUserRole = 'buyer';
              } else if (rolesArray.includes('Supplier')) {
                safeUserRole = 'supplier';
              }
            }
          } catch (e) {
            console.warn('TalkJS: Could not parse applicableRoles:', e);
          }
        }
      }

      // Check if this is an RFQ conversation by checking conversation ID format
      // Format: rfq_<rfqId>_<user1>_<user2>
      const isRfqConversation = conversationId.startsWith('rfq_');
      let plainRfqId: string | null = null;
      if (isRfqConversation) {
        const parts = conversationId.split('_');
        if (parts.length >= 2 && parts[1] !== '0') {
          plainRfqId = parts[1];
        }
      }

      // Access conversation custom data - try multiple ways to get existing attributes
      const conversationAny = conversation as any;
      let customData: any = { ...selectedCustom };

      if (conversationAny.custom) {
        customData = { ...customData, ...conversationAny.custom };
      } else if (conversationAny.attributes?.custom) {
        customData = { ...customData, ...conversationAny.attributes.custom };
      }

      if (isTalkJsReadOnlyConversation({ custom: customData, subject: selectedSubject })) {
        return;
      }

      // Wait a bit for attributes to load if custom data is empty but we have an RFQ conversation
      if (isRfqConversation && (!customData.rfqId || customData.rfqId === '')) {
        await new Promise(resolve => setTimeout(resolve, 500));

        if (conversationAny.custom) {
          customData = { ...selectedCustom, ...conversationAny.custom };
        } else if (conversationAny.attributes?.custom) {
          customData = { ...selectedCustom, ...conversationAny.attributes.custom };
        }

        if (isTalkJsReadOnlyConversation({ custom: customData, subject: selectedSubject })) {
          return;
        }
      }

      // If still no custom data but we have rfqId from conversation ID, use it
      if (isRfqConversation && plainRfqId && (!customData.rfqId || customData.rfqId === '')) {
        customData.rfqId = plainRfqId;
        console.log('TalkJS: Extracted rfqId from conversation ID:', plainRfqId);
      }

      // Only update if this is an RFQ conversation (has rfqId)
      if (!customData.rfqId || customData.rfqId === '') {
        // Not an RFQ conversation, just update user_role
        // Preserve all existing custom data
        const updateAttributes: any = {
          custom: {
            ...customData,
            user_role: safeUserRole
          }
        };
        conversation.setAttributes(updateAttributes);
        console.log('TalkJS: Updated user_role for non-RFQ conversation:', safeUserRole);
        return;
      }

      // This is an RFQ conversation - update user_role and rfq_url
      let rfqUrlBuyer = customData.rfq_url_buyer || '';
      let rfqUrlSupplier = customData.rfq_url_supplier || '';
      let rfqGuid = customData.rfq_guid || '';

      // Helper function to extract rfqId (rfqGuid) from URL
      const extractRfqGuidFromUrl = (url: string): string => {
        if (!url) return '';
        try {
          const urlObj = new URL(url);
          const hash = urlObj.hash;
          const params = new URLSearchParams(hash.split('?')[1] || '');
          const rfqIdParam = params.get('rfqId');
          if (rfqIdParam) {
            return decodeURIComponent(rfqIdParam);
          }
        } catch (e) {
          console.warn('TalkJS: Could not extract rfqGuid from URL:', e);
        }
        return '';
      };

      // If rfqGuid is missing, try to extract it from existing URLs
      if (!rfqGuid || rfqGuid.trim() === '') {
        if (rfqUrlBuyer) {
          rfqGuid = extractRfqGuidFromUrl(rfqUrlBuyer);
        }
        if ((!rfqGuid || rfqGuid.trim() === '') && rfqUrlSupplier) {
          rfqGuid = extractRfqGuidFromUrl(rfqUrlSupplier);
        }
        if (rfqGuid) {
          console.log('TalkJS: Extracted rfqGuid from URL:', rfqGuid);
        }
      }

      // If URLs are missing but we have rfqGuid, reconstruct them
      if ((!rfqUrlBuyer || !rfqUrlSupplier) && rfqGuid) {
        rfqUrlBuyer = `${window.location.origin}/#/rfq/rfqdetail?rfqId=${encodeURIComponent(rfqGuid)}&quotes=Quotes`;
        rfqUrlSupplier = `${window.location.origin}/#/supplier/supplierRfqDetails?rfqId=${encodeURIComponent(rfqGuid)}&rfq=RFQ`;
        console.log('TalkJS: Reconstructed URLs from rfqGuid');
      } else if ((!rfqUrlBuyer || !rfqUrlSupplier) && plainRfqId) {
        // Last resort: try to get rfqGuid from storage or encrypt rfqId
        const rfqGuidFromStorage = AppStorageUtil.getItem('rfqGuid') || '';
        if (rfqGuidFromStorage) {
          rfqGuid = rfqGuidFromStorage;
          rfqUrlBuyer = `${window.location.origin}/#/rfq/rfqdetail?rfqId=${encodeURIComponent(rfqGuid)}&quotes=Quotes`;
          rfqUrlSupplier = `${window.location.origin}/#/supplier/supplierRfqDetails?rfqId=${encodeURIComponent(rfqGuid)}&rfq=RFQ`;
          console.log('TalkJS: Reconstructed URLs from storage rfqGuid');
        } else {
          // Try to encrypt plain rfqId
          try {
            const encryptedRfqId = this._profileService.encrypt(plainRfqId).toString();
            rfqGuid = encryptedRfqId;
            rfqUrlBuyer = `${window.location.origin}/#/rfq/rfqdetail?rfqId=${encodeURIComponent(rfqGuid)}&quotes=Quotes`;
            rfqUrlSupplier = `${window.location.origin}/#/supplier/supplierRfqDetails?rfqId=${encodeURIComponent(rfqGuid)}&rfq=RFQ`;
            console.log('TalkJS: Reconstructed URLs using encrypted rfqId');
          } catch (error) {
            console.warn('TalkJS: Could not encrypt rfqId to reconstruct URLs:', error);
          }
        }
      }

      // Set rfq_url based on current user's role
      let rfqUrl = '';
      if (safeUserRole === 'buyer' && rfqUrlBuyer) {
        rfqUrl = rfqUrlBuyer;
      } else if (safeUserRole === 'supplier' && rfqUrlSupplier) {
        rfqUrl = rfqUrlSupplier;
      } else {
        // Fallback: use whichever URL is available
        rfqUrl = rfqUrlBuyer || rfqUrlSupplier || '';
      }

      // Preserve all existing custom data (including mode) and update user_role and rfq_url
      const updateAttributes: any = {
        custom: {
          ...customData,
          user_role: safeUserRole,
          rfq_url: rfqUrl,
          rfq_url_buyer: rfqUrlBuyer,
          rfq_url_supplier: rfqUrlSupplier
        }
      };
      if (customData.mode) {
        updateAttributes.custom.mode = customData.mode;
      }

      // Always include rfq_guid if we have it (required by backend)
      if (rfqGuid && rfqGuid.trim() !== '') {
        updateAttributes.custom.rfq_guid = rfqGuid;
      }

      // If we extracted rfqId from conversation ID, make sure it's set
      if (plainRfqId && !updateAttributes.custom.rfqId) {
        updateAttributes.custom.rfqId = String(plainRfqId);
      }

      conversation.setAttributes(updateAttributes);
      console.log('TalkJS: Updated conversation attributes for current user:', {
        user_role: safeUserRole,
        rfq_url: rfqUrl,
        rfq_url_buyer: rfqUrlBuyer,
        rfq_url_supplier: rfqUrlSupplier,
        rfq_guid: rfqGuid,
        conversationId: conversationId
      });

      // Only sync with backend if we have rfqGuid (same logic as initializeChat)
      // This prevents 500 errors from the backend when required fields are missing
      const shouldSyncWithBackend = rfqGuid && rfqGuid.trim() !== '';
      if (shouldSyncWithBackend) {
        try {
          await this.createOrUpdateConversation(conversationId, updateAttributes).toPromise();
          console.log('TalkJS: Successfully synced updated conversation attributes with backend');
        } catch (error) {
          console.error('TalkJS: Failed to sync updated conversation attributes with backend:', error);
          // Don't throw - local update is still applied
        }
      } else {
        console.warn('TalkJS: Skipping backend sync - rfqGuid is missing');
        console.warn('TalkJS: Local update applied, but backend sync skipped to avoid 500 error');
      }

    } catch (error) {
      console.error('TalkJS: Error updating conversation attributes for current user:', error);
    }
  }
}

